import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {SupplementalType, toSupplementalType} from "./supplemental-type";

export const ObjectDocumentAssocValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    supplementalType: t.string,
    supplementalGroup: t.string,
    url: t.string,
  }),
  t.partial({
    description: t.string,
    fileName: t.string,
    personId: t.string,
    personName: t.string,
    reportable: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ObjectDocumentAssocArrayValidator = t.array(ObjectDocumentAssocValidator);

export type ObjectDocumentAssoc = t.TypeOf<typeof ObjectDocumentAssocValidator>;
export type ObjectDocumentAssocArray = t.TypeOf<typeof ObjectDocumentAssocArrayValidator>;

@Exclude()
export class ObjectDocumentAssocEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toSupplementalType))
  supplementalType!: SupplementalType;

  @Expose()
  supplementalGroup!: string

  @Expose()
  url!: string;

  @Expose()
  description?: string

  @Expose()
  fileName?: string

  @Expose()
  personId?: string;

  @Expose()
  personName?: string

  @Expose()
  reportable?: boolean

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'OBJECTDOCASSOC';

  get displayName(): Readonly<string> {
    let name = ""

    if (this.fileName) {
      name += this.fileName
      name += " - "
    }

    name += (this.description ? this.description : "No Description")

    return name;
  }

  /**
   * Constructor for ObjectDocumentAssocEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ObjectDocumentAssoc): ObjectDocumentAssocEntity {
    return plainToInstance(ObjectDocumentAssocEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ObjectDocumentAssoc> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ObjectDocumentAssocValidator, object);
  }

  asTypeValue(): ObjectDocumentAssoc {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ObjectDocumentAssocValidator, object);
  }
}

export const ObjectDocumentUploadDTOValidator = t.intersection([
  t.type({
    objectId: t.string,
    objectType: t.string,
  }),
  t.partial({
    description: t.string,
    fileName: t.string,
    personId: t.string,
    reportable: t.boolean,
    rotation: t.number,
    supplementalType: t.string,
  }),
]);

export type ObjectDocumentAssocUploadDTO = t.TypeOf<typeof ObjectDocumentUploadDTOValidator>;

export type ObjectDocumentAssocUpload = ObjectDocumentAssocUploadDTO & {
  file: File
}
